import React, { useState } from "react";
import { Loader2, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SectionHeader from "@/components/shared/SectionHeader";
import { postRegistration } from "@/lib/api";
import { toast } from "sonner";
import { REG_STEPS, PROGRAMMES, COHORTS } from "@/lib/data";
import { EISG } from "@/constants/testIds";

const initial = {
  full_name: "", function: "", organisation: "", country: "", email: "", phone: "",
  programme: "", session: "", participants: 1, needs: "", comments: "",
};

const EISGRegistration = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.programme) {
      toast.error("Merci de renseigner Nom, Email et Programme.");
      return;
    }
    setLoading(true);
    try {
      await postRegistration({ ...form, participants: Number(form.participants) || 1 });
      setDone(true);
      toast.success("Pré-inscription enregistrée. Vous recevrez un email de confirmation.");
    } catch (err) {
      toast.error("Erreur. Réessayez ou contactez contact@bridgedatum.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="py-28 lg:py-36 bg-[#FAFAFA] border-b border-[#0A192F]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader
            number="— Registration"
            eyebrow="Pré-inscription"
            title="Un processus simple, professionnel, transparent."
            subtitle="Six étapes claires : de la pré-inscription à la confirmation finale."
            body="EISG accompagne chaque participant — modalités, facture, visa, logistique."
            titleFont="eisg"
          />
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#0A192F]/10">
            {REG_STEPS.map((s) => (
              <div key={s.num} className="bg-white p-6 text-center">
                <div className="font-eisg text-3xl text-[#D4AF37] leading-none">{s.num}</div>
                <div className="mt-3 font-bd text-sm text-[#0A192F]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline text-[#0A192F]/60 mb-4">— Modalités de paiement</div>
            <h3 className="font-eisg text-3xl text-[#0A192F] tracking-tight leading-tight">
              Acompte 50% pour réserver la place.
            </h3>
            <ul className="mt-8 space-y-3 text-sm text-[#0A192F]/80">
              {[
                "50% d'acompte pour réserver la place",
                "Solde avant le programme",
                "Facture émise après validation",
                "Inscription groupe et institutionnelle disponible",
                "Solutions in-house sur demande",
              ].map((m) => (
                <li key={m} className="flex items-start gap-3"><Check size={16} className="text-[#D4AF37] mt-0.5" /> {m}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <div className="bg-white border border-[#0A192F]/10 p-12 text-center">
                <Check className="mx-auto text-[#D4AF37]" size={40} />
                <h3 className="mt-4 font-eisg text-3xl text-[#0A192F]">Pré-inscription enregistrée</h3>
                <p className="mt-3 text-[#0A192F]/75 max-w-lg mx-auto">
                  Merci {form.full_name}. Notre équipe vous contactera sous 48 heures avec la facture pro-forma et les détails logistiques. Un email de confirmation a été envoyé à {form.email}.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid={EISG.registrationForm} className="bg-white border border-[#0A192F]/10 p-8 lg:p-12">
                <div className="overline text-[#F58220] mb-2">Formulaire</div>
                <h3 className="font-eisg text-2xl text-[#0A192F] tracking-tight mb-8">Pré-inscription EISG</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Nom et prénom *</Label>
                    <Input data-testid={EISG.regName} value={form.full_name} onChange={upd("full_name")} required className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Fonction</Label>
                    <Input data-testid={EISG.regFunction} value={form.function} onChange={upd("function")} className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Organisation</Label>
                    <Input data-testid={EISG.regOrg} value={form.organisation} onChange={upd("organisation")} className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Pays</Label>
                    <Input data-testid={EISG.regCountry} value={form.country} onChange={upd("country")} className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Email *</Label>
                    <Input data-testid={EISG.regEmail} type="email" value={form.email} onChange={upd("email")} required className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Téléphone / WhatsApp</Label>
                    <Input data-testid={EISG.regPhone} value={form.phone} onChange={upd("phone")} className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Programme d'intérêt *</Label>
                    <Select value={form.programme} onValueChange={(v) => setForm((f) => ({ ...f, programme: v }))}>
                      <SelectTrigger data-testid={EISG.regProgramme} className="mt-1 rounded-none border-[#0A192F]/20">
                        <SelectValue placeholder="Choisir un programme" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROGRAMMES.map((p) => (
                          <SelectItem key={p.id} value={p.title}>{p.short} — {p.location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Session souhaitée</Label>
                    <Select value={form.session} onValueChange={(v) => setForm((f) => ({ ...f, session: v }))}>
                      <SelectTrigger data-testid={EISG.regSession} className="mt-1 rounded-none border-[#0A192F]/20">
                        <SelectValue placeholder="Choisir une session" />
                      </SelectTrigger>
                      <SelectContent>
                        {COHORTS.map((c) => (
                          <SelectItem key={c.period} value={c.period}>{c.period} — {c.location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Nombre de participants</Label>
                    <Input data-testid={EISG.regParticipants} type="number" min="1" value={form.participants} onChange={upd("participants")} className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Besoins spécifiques</Label>
                    <Input data-testid={EISG.regNeeds} value={form.needs} onChange={upd("needs")} className="mt-1 rounded-none border-[#0A192F]/20" placeholder="Visa, langue, restauration…" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold">Commentaires / objectifs</Label>
                    <Textarea data-testid={EISG.regComments} rows={4} value={form.comments} onChange={upd("comments")} className="mt-1 rounded-none border-[#0A192F]/20" />
                  </div>
                </div>
                <Button data-testid={EISG.registrationSubmit} type="submit" disabled={loading} className="mt-8 bg-[#0A192F] hover:bg-[#001529] text-white rounded-none h-12 px-10 font-semibold tracking-wider text-xs uppercase">
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={14} className="mr-2" />}
                  Envoyer la pré-inscription
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EISGRegistration;
